import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { MobileTopBar } from "./mobile-top-bar";
import { MobileBottomTabs } from "./mobile-bottom-tabs";
import { MobileDrawer } from "./mobile-drawer";
import { MobileAgentSheet } from "./mobile-agent-sheet";
import { MobileChannelSheet } from "./mobile-channel-sheet";
import { MobileChannelContext } from "./mobile-channel-context";
import { MobileChatHeader } from "@/components/mobile/mobile-chat-header";
import "@/styles/mobile-glass.css";

/**
 * Mobile-only shell. Renders above content:
 *   - Screen chrome: either the generic MobileTopBar, the MobileChatHeader
 *     (on /workspace), or nothing (canonical screens provide their own header).
 *   - Outlet (current route)
 *   - 5-tab bottom bar: Home · Chat · Agents · Inbox · More
 */
export function MobileShell() {
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlAgentId = searchParams.get("agentId");
  const pathAgentMatch = location.pathname.match(/\/agents\/([^/]+)/);
  const pathAgentId = pathAgentMatch?.[1];
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    urlAgentId || pathAgentId || null,
  );

  useEffect(() => {
    const newId = urlAgentId || pathAgentId || null;
    if (newId && newId !== selectedAgentId) setSelectedAgentId(newId);
  }, [urlAgentId, pathAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedAgentId && agents.length > 0) {
      const first = agents.find((a) => !a.parentAgentId) || agents[0];
      setSelectedAgentId(first.id);
      if (location.pathname.endsWith("/workspace") && !urlAgentId) {
        navigate(`${location.pathname}?agentId=${first.id}`, { replace: true });
      }
    }
  }, [agents, selectedAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [agentSheetOpen, setAgentSheetOpen] = useState(false);
  const [channelSheetOpen, setChannelSheetOpen] = useState(false);

  const urlChannelId = searchParams.get("channelId") || "general";
  const urlSessionId = searchParams.get("sessionId");

  // Which header to show. The four canonical rework screens (Home, Agents,
  // Inbox) render their own tall header — keep the shell chrome minimal.
  // Workspace (chat) gets the identity-as-title MobileChatHeader.
  const base = `/tenants/${tenantId}`;
  const isChat =
    location.pathname === `${base}/workspace` ||
    location.pathname === `${base}/chat`;
  const isCanonical =
    location.pathname === `${base}/dashboard` ||
    location.pathname === `${base}/agents` ||
    location.pathname === `${base}/inbox`;

  return (
    <MobileChannelContext.Provider
      value={{
        openChannelSheet: () => setChannelSheetOpen(true),
        openDrawer: () => setDrawerOpen(true),
        openAgentSheet: () => setAgentSheetOpen(true),
      }}
    >
      <div className="mobile-aurora relative flex h-[100dvh] w-screen flex-col overflow-hidden sm:hidden">
        {/* Aurora backdrop: base gradient + radial washes + drifting blobs.
            Sits behind all content, never captures pointer events. */}
        <div className="mg-backdrop" aria-hidden="true">
          <div className="mg-blob mg-blob-1" />
          <div className="mg-blob mg-blob-2" />
          <div className="mg-blob mg-blob-3" />
        </div>

        {isChat ? (
          <MobileChatHeader
            selectedAgentId={selectedAgentId}
            onOpenAgentSheet={() => setAgentSheetOpen(true)}
          />
        ) : isCanonical ? null : (
          <MobileTopBar
            selectedAgentId={selectedAgentId}
            onOpenDrawer={() => setDrawerOpen(true)}
            onOpenAgentSheet={() => setAgentSheetOpen(true)}
          />
        )}

        {/* The bottom tab bar now floats over content. Canonical Aurora
            screens scroll behind it (they pad themselves); every other
            route keeps clear of the capsule via bottom padding. */}
        <main
          className="mg-above-backdrop flex-1 min-h-0 overflow-hidden"
          style={
            isCanonical
              ? undefined
              : { paddingBottom: "calc(84px + env(safe-area-inset-bottom, 0px))" }
          }
        >
          <Outlet />
        </main>

        <MobileBottomTabs
          selectedAgentId={selectedAgentId}
          onOpenAgentSheet={() => setAgentSheetOpen(true)}
          onOpenDrawer={() => setDrawerOpen(true)}
        />

        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        <MobileAgentSheet
          open={agentSheetOpen}
          onClose={() => setAgentSheetOpen(false)}
          selectedAgentId={selectedAgentId}
          onSelect={setSelectedAgentId}
        />
        <MobileChannelSheet
          open={channelSheetOpen}
          onClose={() => setChannelSheetOpen(false)}
          agentId={selectedAgentId}
          activeChannelId={urlChannelId}
          activeSessionId={urlSessionId}
        />
      </div>
    </MobileChannelContext.Provider>
  );
}
